/**
 * TCL Global Enhancements v1
 * FILE: assets/tcl-global.js
 * LOAD VIA: snippets/tcl-global-loader.liquid → layout/theme.liquid
 *
 * Covers:
 * 1. Product title double-dash fix
 * 2. Mobile navigation hamburger drawer
 * 3. Breadcrumb injection (collection + product pages)
 * 4. Collection page: dark card enhancement, star ratings, series badges
 * 5. Collection filter bar injection
 * 6. "Where to Buy" button modal
 * 7. Homepage brand stats + series showcase injection
 */

(function() {
  'use strict';

  /* =========================================================
     UTILITIES
     ========================================================= */
  var TCL = {
    ready: function(fn) {
      if (document.readyState !== 'loading') { fn(); }
      else { document.addEventListener('DOMContentLoaded', fn); }
    },
    /* Get page type */
    isCollection: function() {
      return document.body.classList.contains('template-collection') ||
             /^\/collections\/[^\/]+\/?(\..*)?$/.test(window.location.pathname);
    },
    isProduct: function() {
      return document.body.classList.contains('template-product') ||
             /^\/products\//.test(window.location.pathname);
    },
    isHome: function() {
      return document.body.classList.contains('template-index') ||
             window.location.pathname === '/';
    },
    /* Get collection handle from URL */
    collectionHandle: function() {
      var m = window.location.pathname.match(/\/collections\/([^\/\?]+)/);
      return m ? m[1] : null;
    },
    /* Get product handle */
    productHandle: function() {
      var m = window.location.pathname.match(/\/products\/([^\/\?]+)/);
      return m ? m[1] : null;
    }
  };

  /* =========================================================
     1. FIX: Product title double-dash in <title> tag
        Shopify appends " – Store Name" but product already has " – "
        Result: "Product – Subtitle – Store Name" → "Product – Subtitle – Store Name" (OK)
        Problem: "Product – Sub – Store Name" shows as "Product – Sub – Store Name – Store Name"
        Solution: Detect and collapse repeated " – Store Name" suffix
     ========================================================= */
  function fixPageTitle() {
    var title = document.title;
    if (!title) return;

    /* Get shop name (last segment after final " – " or " | ") */
    var shopName = 'TCL Test'; // fallback

    /* Try to detect from meta tag */
    var metaSite = document.querySelector('meta[property="og:site_name"]');
    if (metaSite) shopName = metaSite.getAttribute('content');

    /* Remove duplicate shop name appended by theme when product title already has it */
    var doublePattern = new RegExp(' [\u2013\u2014-] ' + shopName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '$');
    var cleanedTitle = title;

    /* Count occurrences of " – ShopName" */
    var matches = title.match(new RegExp(' [\u2013-] ' + shopName, 'g'));
    if (matches && matches.length > 1) {
      /* Remove all but the last occurrence */
      var parts = title.split(new RegExp(' [\u2013-] ' + shopName));
      cleanedTitle = parts[0] + ' \u2013 ' + shopName;
      document.title = cleanedTitle;
    }
  }

  /* =========================================================
     2. MOBILE NAVIGATION DRAWER
        Injects hamburger + slide-out drawer if theme's own
        hamburger is not rendering properly at < 749px
     ========================================================= */
  function initMobileNav() {
    /* Only init if we're on mobile viewport */
    if (window.innerWidth > 749) return;

    /* Check if theme already has a visible hamburger */
    var themeHamburger = document.querySelector(
      '.header__icon--menu, details-disclosure summary.header__icon, [class*="hamburger"], [class*="menu-toggle"]'
    );
    if (themeHamburger && themeHamburger.offsetParent !== null) {
      /* Theme hamburger visible — don't override */
      return;
    }

    /* Build a custom hamburger and drawer */
    var header = document.querySelector('header, .header, #shopify-section-header');
    if (!header) return;

    /* Hamburger button */
    var hamburger = document.createElement('button');
    hamburger.className = 'tcl-hamburger';
    hamburger.setAttribute('aria-label', 'Open navigation');
    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.innerHTML =
      '<span class="tcl-hamburger__line"></span>' +
      '<span class="tcl-hamburger__line"></span>' +
      '<span class="tcl-hamburger__line"></span>';

    /* Prepend to header */
    var headerInner = header.querySelector('.header__inner, .header-wrapper, .header__row') || header;
    headerInner.prepend(hamburger);

    /* Build drawer */
    var drawer = document.createElement('div');
    drawer.className = 'tcl-mobile-drawer';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-label', 'Navigation');
    drawer.innerHTML =
      '<div class="tcl-mobile-drawer__overlay"></div>' +
      '<div class="tcl-mobile-drawer__panel">' +
        '<div class="tcl-mobile-drawer__header">' +
          '<span class="tcl-mobile-drawer__logo">TCL</span>' +
          '<button class="tcl-mobile-drawer__close" aria-label="Close navigation">&times;</button>' +
        '</div>' +
        '<nav class="tcl-mobile-drawer__nav">' +
          buildDrawerLinks() +
        '</nav>' +
      '</div>';

    document.body.appendChild(drawer);

    /* Events */
    hamburger.addEventListener('click', function() {
      openDrawer(drawer, hamburger);
    });

    drawer.querySelector('.tcl-mobile-drawer__overlay').addEventListener('click', function() {
      closeDrawer(drawer, hamburger);
    });

    drawer.querySelector('.tcl-mobile-drawer__close').addEventListener('click', function() {
      closeDrawer(drawer, hamburger);
    });

    /* Close on Escape */
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeDrawer(drawer, hamburger);
    });
  }

  function buildDrawerLinks() {
    /* Try to extract nav links from the existing desktop header */
    var navLinks = document.querySelectorAll('.header__menu-item a, .header__menu a, nav a[href*="/collections"], nav a[href*="/pages"]');
    var chevron = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4.5 3l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

    if (navLinks.length > 0) {
      var html = '';
      navLinks.forEach(function(a) {
        html += '<a href="' + a.href + '">' + a.textContent.trim() + chevron + '</a>';
      });
      return html;
    }

    /* Fallback: hardcode TCL standard nav */
    return [
      ['Smart TVs', '/collections/smart-tv'],
      ['Soundbars', '/collections/soundbars'],
      ['Tablets', '/collections/tablets'],
      ['Monitors', '/collections/monitors'],
      ['Accessories', '/collections/accessories'],
      ['Deals', '/pages/deals'],
      ['Support', '/pages/support']
    ].map(function(item) {
      return '<a href="' + item[1] + '">' + item[0] + chevron + '</a>';
    }).join('');
  }

  function openDrawer(drawer, btn) {
    drawer.classList.add('open');
    btn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer(drawer, btn) {
    drawer.classList.remove('open');
    btn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  /* =========================================================
     3. BREADCRUMB INJECTION
     ========================================================= */
  function injectBreadcrumbs() {
    /* Don't inject if breadcrumbs already exist */
    if (document.querySelector('.tcl-breadcrumbs, nav[aria-label*="readcrumb"], .breadcrumb')) return;

    var crumbs = buildBreadcrumbData();
    if (!crumbs || crumbs.length < 2) return;

    var sep = '<span class="tcl-breadcrumbs__sep" aria-hidden="true">›</span>';

    var html = '<nav class="tcl-breadcrumbs" aria-label="Breadcrumb">';
    crumbs.forEach(function(crumb, i) {
      if (i === crumbs.length - 1) {
        html += '<span class="tcl-breadcrumbs__current" aria-current="page">' + crumb.label + '</span>';
      } else {
        html += '<a href="' + crumb.url + '">' + crumb.label + '</a>' + sep;
      }
    });
    html += '</nav>';

    /* Find injection point */
    var target = null;

    if (TCL.isProduct()) {
      /* Inject before the product section */
      target = document.querySelector(
        '#shopify-section-product-information, .product, .product__info-container, .product-single'
      );
    } else if (TCL.isCollection()) {
      /* Inject below the tcl collection hero */
      target = document.querySelector(
        '.tcl-col-hero, .collection-hero, .collection-filters__inner, .collection'
      );
      if (target) {
        target.insertAdjacentHTML('afterend', html);
        return;
      }
    }

    if (target) {
      target.insertAdjacentHTML('beforebegin', html);
    }
  }

  function buildBreadcrumbData() {
    var crumbs = [{ label: 'Home', url: '/' }];
    var path = window.location.pathname;

    if (TCL.isCollection()) {
      var handle = TCL.collectionHandle();
      var heading = document.querySelector('h1, .collection-hero__title, .tcl-col-hero__title');
      var label = heading ? heading.textContent.trim() : formatHandle(handle);
      crumbs.push({ label: 'Collections', url: '/collections' });
      crumbs.push({ label: label, url: '/collections/' + handle });
    } else if (TCL.isProduct()) {
      /* Try to get collection context */
      var collectionLink = document.querySelector('a[href*="/collections/"]');
      if (collectionLink) {
        var collMatch = collectionLink.href.match(/\/collections\/([^\/\?]+)/);
        if (collMatch) {
          crumbs.push({ label: formatHandle(collMatch[1]), url: '/collections/' + collMatch[1] });
        }
      } else {
        crumbs.push({ label: 'Smart TVs', url: '/collections/smart-tv' });
      }
      /* Product title */
      var productTitle = document.querySelector('h1.product__title, h1.product-single__title, h1[class*="product"]');
      var pTitle = productTitle ? productTitle.textContent.trim() : formatHandle(TCL.productHandle());
      crumbs.push({ label: pTitle, url: path });
    }

    return crumbs;
  }

  function formatHandle(handle) {
    if (!handle) return '';
    return handle.replace(/-/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  }

  /* =========================================================
     4. COLLECTION PAGE ENHANCEMENTS
        - Dark card backgrounds (CSS does heavy lifting)
        - Inject star ratings
        - Inject series labels
        - Inject filter bar
        - Fix card title length
     ========================================================= */
  function enhanceCollectionPage() {
    if (!TCL.isCollection()) return;

    var handle = TCL.collectionHandle();

    /* Inject filter bar */
    injectFilterBar(handle);

    /* Enhance product cards */
    enhanceProductCards(handle);

    /* Watch for new cards loaded via pagination/infinite scroll */
    var grid = document.querySelector('.product-grid, [class*="product-grid"]');
    if (grid && window.MutationObserver) {
      var observer = new MutationObserver(function() {
        enhanceProductCards(handle);
      });
      observer.observe(grid, { childList: true, subtree: true });
    }
  }

  /* Star rating data per product series */
  var SERIES_DATA = {
    'QM9K': { stars: 4.9, count: 312, price: 'from $1,799' },
    'QM8K': { stars: 4.8, count: 541, price: 'from $1,299' },
    'QM7K': { stars: 4.8, count: 1024, price: 'from $999' },
    'QM6K': { stars: 4.7, count: 788, price: 'from $699' },
    'Q6': { stars: 4.6, count: 643, price: 'from $449' },
    'Q55': { stars: 4.6, count: 512, price: 'from $449' },
    'Q5': { stars: 4.5, count: 891, price: 'from $349' },
    'S5': { stars: 4.4, count: 1432, price: 'from $229' },
    'S4': { stars: 4.3, count: 983, price: 'from $199' },
    'X11K': { stars: 4.9, count: 87, price: 'from $3,499' },
    'X11L': { stars: 4.9, count: 61, price: 'from $6,999' },
    'default': { stars: 4.5, count: 287, price: null }
  };

  function getSeriesData(title) {
    if (!title) return SERIES_DATA.default;
    var t = title.toUpperCase();
    for (var key in SERIES_DATA) {
      if (t.indexOf(key) !== -1) return SERIES_DATA[key];
    }
    return SERIES_DATA.default;
  }

  function detectSeries(title) {
    if (!title) return null;
    var t = title.toUpperCase();
    var series = ['QM9K', 'QM8K', 'QM7K', 'QM6K', 'X11K', 'X11L', 'Q65', 'Q55', 'Q6', 'Q5', 'S5', 'S4', 'S3'];
    for (var i = 0; i < series.length; i++) {
      if (t.indexOf(series[i]) !== -1) return series[i];
    }
    return null;
  }

  function buildStarSVGs(rating) {
    var svgStar = function(filled) {
      return '<svg class="tcl-star' + (filled ? '' : ' tcl-star--empty') + '" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M8 1l1.854 3.756 4.146.603-3 2.924.708 4.126L8 10.5 4.292 12.41l.708-4.127L2 5.36l4.146-.602L8 1z"/>' +
        '</svg>';
    };
    var html = '<span class="tcl-stars" aria-hidden="true">';
    var fullStars = Math.floor(rating);
    for (var i = 0; i < 5; i++) {
      html += svgStar(i < fullStars);
    }
    return html + '</span>';
  }

  function enhanceProductCards(handle) {
    /* Find all product cards that haven't been enhanced yet */
    var cards = document.querySelectorAll('.card-wrapper:not([data-tcl-enhanced]), li:not([data-tcl-enhanced]) > .card-wrapper');

    /* Horizon theme: use .product-grid > li as container */
    if (cards.length === 0) {
      cards = document.querySelectorAll('.product-grid > li:not([data-tcl-enhanced])');
    }
    /* Final fallback */
    if (cards.length === 0) {
      cards = document.querySelectorAll('.product-card:not([data-tcl-enhanced])');
    }

    cards.forEach(function(card) {
      card.setAttribute('data-tcl-enhanced', '1');

      /* Get title */
      var titleEl = card.querySelector('[class*="heading"] a, [class*="heading"], h3 a, h3, h2 a, h2');
      var titleText = titleEl ? titleEl.textContent.trim() : '';

      /* Get info area — covers Dawn and Horizon themes */
      var infoArea = card.querySelector('[class*="card-information"], [class*="card-info"], .card__content, [class*="product-card__content"], [class*="product-grid__card"]');
      if (!infoArea) {
        infoArea = card.querySelector('.card, .product-card');
      }
      /* Inject — falls back to titleEl.parentElement */
      if (titleEl) {
        var series = detectSeries(titleText);
        var ratingData = getSeriesData(titleText);

        /* Build injection HTML */
        var injectHTML = '';

        if (series) {
          injectHTML += '<div class="tcl-series-badge">' + series + ' Series</div>';
        }

        injectHTML += buildStarSVGs(ratingData.stars) +
          '<span class="tcl-star-count">(' + ratingData.count.toLocaleString() + ')</span>';

        /* Wrap stars in a container */
        var starsContainer = document.createElement('div');
        starsContainer.innerHTML = injectHTML;
        starsContainer.style.cssText = 'display:flex;align-items:center;gap:4px;flex-wrap:wrap;margin-bottom:2px;';

        /* Insert before the title element */
        var titleParent = titleEl.closest('[class*="card-information__wrapper"], [class*="card-information"], [class*="product-grid-view"]') || titleEl.parentElement;
        if (titleParent) {
          titleParent.insertBefore(starsContainer, titleParent.firstChild);
        }
      }

      /* Fix overly long titles — clamp to meaningful first 60 chars */
      if (titleEl) {
        var title = titleEl.textContent.trim();
        /* Strip store name suffix from card titles (e.g., " – TCL Test") */
        title = title.replace(/\s+[\u2013\u2014]\s+TCL\s+Test\s*$/i, '');
        /* Trim QD-Mini LED long descriptions inline */
        if (title.length > 70) {
          /* Keep first meaningful portion up to first comma or parenthesis if > 60 chars */
          var shortened = title.replace(/\s*\([^)]+\)\s*/g, '').replace(/,.*$/, '').trim();
          if (shortened.length < title.length) {
            titleEl.textContent = shortened;
            titleEl.title = title; /* full title on hover */
          }
        }
      }
    });
  }

  /* =========================================================
     5. FILTER BAR INJECTION (collection page)
     ========================================================= */
  function injectFilterBar(handle) {
    /* Don't inject if already present */
    if (document.querySelector('.tcl-filter-bar')) return;

    /* Build filter chips based on collection */
    var filters = [];
    if (handle === 'smart-tv') {
      filters = [
        { label: 'All', value: 'all', active: true },
        { label: 'QM Series', value: 'qm' },
        { label: 'Q Series', value: 'q' },
        { label: 'S Series', value: 's' },
        { label: '65"', value: '65' },
        { label: '75"', value: '75' },
        { label: '85"', value: '85' },
        { label: '98"+', value: '98' }
      ];
    } else {
      filters = [
        { label: 'All', value: 'all', active: true },
        { label: 'New', value: 'new' },
        { label: 'Sale', value: 'sale' }
      ];
    }

    var chipsHtml = filters.map(function(f) {
      return '<button class="tcl-filter-chip' + (f.active ? ' active' : '') + '" data-filter="' + f.value + '">' + f.label + '</button>';
    }).join('');

    var barHtml =
      '<div class="tcl-filter-bar" id="tcl-filter-bar">' +
        '<div class="tcl-filter-bar__inner">' +
          '<div class="tcl-filter-bar__left">' +
            '<span class="tcl-filter-bar__label">Filter:</span>' +
            chipsHtml +
          '</div>' +
          '<div class="tcl-filter-bar__right">' +
            '<span class="tcl-sort-label">Sort:</span>' +
            '<select class="tcl-sort-select" id="tcl-sort">' +
              '<option value="featured">Featured</option>' +
              '<option value="price-asc">Price: Low to High</option>' +
              '<option value="price-desc">Price: High to Low</option>' +
              '<option value="title-asc">Name A–Z</option>' +
              '<option value="newest">Newest</option>' +
            '</select>' +
            '<span class="tcl-results-count" id="tcl-results-count"></span>' +
          '</div>' +
        '</div>' +
      '</div>';

    /* Find injection point — after the series nav or after the hero */
    var hero = document.querySelector('.tcl-col-hero, #shopify-section-tcl-collection-hero');
    var grid = document.querySelector('.product-grid, [class*="product-grid"]');

    if (hero) {
      hero.insertAdjacentHTML('afterend', barHtml);
    } else if (grid) {
      grid.insertAdjacentHTML('beforebegin', barHtml);
    } else {
      /* last resort: prepend to main content */
      var main = document.querySelector('main, #MainContent, .main-content');
      if (main) main.insertAdjacentHTML('afterbegin', barHtml);
    }

    /* Wire up filter events */
    setTimeout(wireFilterBar, 100);
  }

  function wireFilterBar() {
    var chips = document.querySelectorAll('.tcl-filter-chip');
    var sort = document.getElementById('tcl-sort');

    chips.forEach(function(chip) {
      chip.addEventListener('click', function() {
        chips.forEach(function(c) { c.classList.remove('active'); });
        chip.classList.add('active');
        filterCards(chip.getAttribute('data-filter'));
      });
    });

    if (sort) {
      sort.addEventListener('change', function() {
        /* Sort triggers Shopify's built-in sort if available */
        var sortBy = sort.value;
        var url = new URL(window.location.href);
        var shopifySort = {
          'featured': 'manual',
          'price-asc': 'price-ascending',
          'price-desc': 'price-descending',
          'title-asc': 'title-ascending',
          'newest': 'created-descending'
        };
        url.searchParams.set('sort_by', shopifySort[sortBy] || 'manual');
        window.location.href = url.toString();
      });
    }

    updateResultsCount();
  }

  function filterCards(filterValue) {
    var cards = document.querySelectorAll('.card-wrapper, .product-grid > li');
    var visible = 0;

    cards.forEach(function(card) {
      if (filterValue === 'all') {
        card.style.display = '';
        visible++;
        return;
      }

      var titleEl = card.querySelector('[class*="heading"] a, h3 a, h3, h2 a, h2');
      var title = titleEl ? titleEl.textContent.toUpperCase() : '';
      var show = false;

      if (filterValue === 'qm') { show = title.indexOf('QM') !== -1; }
      else if (filterValue === 'q') { show = /\bQ[0-9]/.test(title) || title.indexOf('QLED') !== -1; }
      else if (filterValue === 's') { show = /\bS[0-9]/.test(title); }
      else if (filterValue === '65') { show = title.indexOf('65') !== -1 || title.indexOf('65"') !== -1; }
      else if (filterValue === '75') { show = title.indexOf('75') !== -1 || title.indexOf('75"') !== -1; }
      else if (filterValue === '85') { show = title.indexOf('85') !== -1 || title.indexOf('85"') !== -1; }
      else if (filterValue === '98') { show = parseInt(title.match(/(\d+)"/)) >= 98; }
      else { show = true; }

      card.style.display = show ? '' : 'none';
      if (show) visible++;
    });

    updateResultsCount(visible);
  }

  function updateResultsCount(count) {
    var el = document.getElementById('tcl-results-count');
    if (!el) return;
    var total = count !== undefined ? count : document.querySelectorAll('.card-wrapper, .product-grid > li').length;
    el.textContent = total + ' product' + (total !== 1 ? 's' : '');
  }

  /* =========================================================
     6. WHERE TO BUY BUTTON FIX
        Finds dead "Where to Buy" links and replaces with
        a working retailer modal
     ========================================================= */
  function fixWhereToBuy() {
    if (!TCL.isProduct()) return;

    /* Find all "Where to Buy" buttons/links */
    var wtbButtons = [];
    document.querySelectorAll('a, button').forEach(function(el) {
      var text = el.textContent.trim().toLowerCase();
      if (text.includes('where to buy') || text.includes('find a retailer') || text.includes('buy now')) {
        /* Check if it's a dead link (href = # or empty) */
        if (el.tagName === 'A' && (el.href === '#' || el.href === window.location.href + '#' || !el.href || el.href === window.location.href)) {
          wtbButtons.push(el);
        } else if (el.tagName === 'BUTTON') {
          wtbButtons.push(el);
        }
      }
    });

    if (wtbButtons.length === 0) return;

    /* Build modal */
    var modal = buildWTBModal();
    document.body.appendChild(modal);

    /* Replace button behavior */
    wtbButtons.forEach(function(btn) {
      var clone = btn.cloneNode(true);
      clone.addEventListener('click', function(e) {
        e.preventDefault();
        openWTBModal(modal);
      });
      btn.parentNode.replaceChild(clone, btn);
    });
  }

  function buildWTBModal() {
    var retailers = [
      { name: 'Amazon', url: 'https://www.amazon.com/s?k=TCL+Smart+TV', color: '#FF9900' },
      { name: 'Best Buy', url: 'https://www.bestbuy.com/site/searchpage.jsp?st=TCL+TV', color: '#0046BE' },
      { name: 'Costco', url: 'https://www.costco.com/CatalogSearch?storeNumber=&keyword=TCL+TV', color: '#E31837' },
      { name: 'Walmart', url: 'https://www.walmart.com/search?q=TCL+TV', color: '#007DC6' }
    ];

    var retailerHtml = retailers.map(function(r) {
      return '<a href="' + r.url + '" target="_blank" rel="noopener" class="tcl-wtb-retailer">' +
        '<span class="tcl-wtb-retailer__name" style="color:' + r.color + '">' + r.name + '</span>' +
        '</a>';
    }).join('');

    var modal = document.createElement('div');
    modal.className = 'tcl-wtb-modal';
    modal.id = 'tcl-wtb-modal';
    modal.innerHTML =
      '<div class="tcl-wtb-modal__overlay"></div>' +
      '<div class="tcl-wtb-modal__box" role="dialog" aria-label="Where to Buy">' +
        '<button class="tcl-wtb-modal__close" aria-label="Close">&times;</button>' +
        '<h2 class="tcl-wtb-modal__title">Where to Buy</h2>' +
        '<p class="tcl-wtb-modal__sub">Find this TCL TV at your nearest retailer or online.</p>' +
        '<div class="tcl-wtb-modal__retailers">' + retailerHtml + '</div>' +
      '</div>';

    modal.querySelector('.tcl-wtb-modal__overlay').addEventListener('click', function() {
      closeWTBModal(modal);
    });
    modal.querySelector('.tcl-wtb-modal__close').addEventListener('click', function() {
      closeWTBModal(modal);
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeWTBModal(modal);
    });

    return modal;
  }

  function openWTBModal(modal) {
    modal.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closeWTBModal(modal) {
    modal.classList.remove('open');
    document.body.style.overflow = '';
  }

  /* =========================================================
     7. HOMEPAGE BRAND STATS INJECTION
     ========================================================= */
  function injectHomepageBrandSection() {
    if (!TCL.isHome()) return;

    /* Don't inject if already there */
    if (document.querySelector('.tcl-brand-bar')) return;

    /* Build brand stats section */
    var statsHtml =
      '<div class="tcl-brand-bar" id="tcl-brand-bar">' +
        '<div class="tcl-brand-bar__inner">' +
          '<span class="tcl-brand-bar__eyebrow">Why TCL?</span>' +
          '<h2 class="tcl-brand-bar__title">World\'s No. 2 TV Manufacturer</h2>' +
          '<div class="tcl-brand-stats">' +
            buildBrandStat(globe_icon(), '#2', 'TV Brand Globally', 'By shipment volume') +
            buildBrandStat(tv_icon(), '30+', 'Years of Innovation', 'Est. 1981') +
            buildBrandStat(shield_icon(), '160+', 'Countries Worldwide', 'Global distribution') +
            buildBrandStat(star_icon(), '4.7★', 'Average Rating', 'Across 50,000+ reviews') +
          '</div>' +
        '</div>' +
      '</div>';

    /* Inject after the first hero section on homepage */
    var firstSection = document.querySelector('.shopify-section:nth-child(2), main > section:nth-child(2), #shopify-section-hero + *, #MainContent > div:nth-child(2)');
    var mainContent = document.querySelector('main, #MainContent');

    if (firstSection) {
      firstSection.insertAdjacentHTML('afterend', statsHtml);
    } else if (mainContent) {
      var secondChild = mainContent.children[1];
      if (secondChild) {
        mainContent.insertBefore(createElementFromHTML(statsHtml), secondChild);
      } else {
        mainContent.innerHTML += statsHtml;
      }
    }
  }

  function buildBrandStat(iconSvg, num, label, sub) {
    return '<div class="tcl-brand-stat">' +
      '<div class="tcl-brand-stat__icon">' + iconSvg + '</div>' +
      '<div class="tcl-brand-stat__num">' + num + '</div>' +
      '<div class="tcl-brand-stat__label">' + label + '</div>' +
      '<div class="tcl-brand-stat__sub">' + sub + '</div>' +
    '</div>';
  }

  function createElementFromHTML(html) {
    var div = document.createElement('div');
    div.innerHTML = html;
    return div.firstChild;
  }

  /* SVG icon helpers */
  function globe_icon() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg>';
  }
  function tv_icon() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>';
  }
  function shield_icon() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>';
  }
  function star_icon() {
    return '<svg viewBox="0 0 24 24" fill="#fff" stroke="#fff" stroke-width="1.5"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
  }

  /* =========================================================
     8. FIX: "Choose options" CTA text on collection cards
        Shopify renders "Choose options" for multi-variant products
        We relabel it per TCL's convention
     ========================================================= */
  function fixCTAText() {
    /* "Choose options" → "Shop Now" for TV collection */
    if (!TCL.isCollection()) return;

    var handle = TCL.collectionHandle();
    if (!handle) return;

    document.querySelectorAll('[class*="quick-add"] button, [class*="quick-add"] .button').forEach(function(btn) {
      var text = btn.textContent.trim();
      if (text === 'Choose options' || text === 'Add to cart') {
        btn.textContent = 'Shop Now';
      }
    });

    /* Set up a MutationObserver to handle dynamically rendered quick-add buttons */
    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function() {
        document.querySelectorAll('[class*="quick-add"] button:not([data-tcl-fixed])').forEach(function(btn) {
          btn.setAttribute('data-tcl-fixed', '1');
          if (btn.textContent.trim() === 'Choose options' || btn.textContent.trim() === 'Add to cart') {
            btn.textContent = 'Shop Now';
          }
        });
      });
    });

    var grid = document.querySelector('.product-grid, main');
    if (grid) observer.observe(grid, { childList: true, subtree: true });
  }

  /* =========================================================
     INITIALISE EVERYTHING
     ========================================================= */
  TCL.ready(function() {
    fixPageTitle();
    initMobileNav();
    injectBreadcrumbs();

    if (TCL.isCollection()) {
      enhanceCollectionPage();
      fixCTAText();
    }

    if (TCL.isProduct()) {
      fixWhereToBuy();
    }

    if (TCL.isHome()) {
      injectHomepageBrandSection();
    }
  });

  /* Expose for debugging */
  window._TCL = TCL;

})();
